/**
 * CreditosView -- Abonos de credito (#/creditos).
 *
 * T-CRED-PANEL (NEXO-API-SYNC-EXPAND-001, contrato lockeado #2092 ACK #2114).
 *
 * V1 = ABONOS de credito, NO saldos. Fuente: venta_pagos origen=derivado-credito
 * (fuente unica, sin doble-conteo con credito_abono, convencion 2026-06-07). El
 * CREDITO completo (cliente, monto original, saldo pendiente) NO existe aun:
 * nexo_sync.sync_creditos tiene 0 filas (el uploader Desktop no existe). Cuando
 * suba, V2 agrega /creditos/{id} con el credito real y esta vista gana las
 * columnas cliente/saldo + el registrar-abono.
 *
 * El endpoint espeja el shape de /ingresos (BusinessIngresoItemDto), asi que esta
 * vista es hermana de IngresosView (A-T3): mismo groupBy, mismas columnas, mismos
 * helpers conceptuales.
 *   - groupBy=dia (default): 1 fila por dia. Fecha | Medio | Monto. num_pagos = abonos del dia.
 *   - groupBy=abono: 1 fila por pago. Fecha | Medio | Origen | Monto.
 *
 * Por que se reescribio: el mockup V2 previo pintaba credito-completo (cliente,
 * total, abonado, saldo, estados, tabs detalle, registrar-abono) contra endpoints
 * que NO existen en V1 (getCreditoById, POST /creditos/{id}/abonos) -> empty-state
 * permanente + UI muerta. Se reemplaza por lo que el backend SI entrega hoy.
 *
 * @module presentation/views/CreditosView
 */

import { FinanzasApiClient } from '../../infrastructure/api/FinanzasApiClient.js';
import { getSelectedBusiness } from '../components/BusinessSelector.js';

const PERIODS = [
  { code: 'total',  label: 'Total' },
  { code: 'mes',    label: 'Mes' },
  { code: 'semana', label: 'Semana' },
  { code: 'hoy',    label: 'Hoy' },
  { code: 'rango',  label: 'Rango' }
];
const MEDIOS = [
  { code: '',              label: 'Todos' },
  { code: 'efectivo',      label: 'Efectivo' },
  { code: 'transferencia', label: 'Transferencia' }
];
const GROUP_MODES = [
  { code: 'dia',   label: 'Por día' },
  { code: 'abono', label: 'Por abono' }
];
const PAGE_SIZE = 100;

const MEDIO_COLOR = {
  efectivo:      '#3fb950',
  transferencia: '#58a6ff',
  mixto:         '#d2a8ff'
};

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc as escHtml } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

const formatCurrency = (n) => {
  const num = Number(n) || 0;
  return num.toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

// fecha ISO ("2026-05-31T20:15:00") -> "2026-05-31 20:15"; date-only pasa tal cual.
// Paridad finanzas (REDIRECT Josse #2160): fecha = solo YYYY-MM-DD; la hora vive
// en su columna. Creditos sale de venta_pagos (fecha DATE, sin hora) -> columna
// Hora muestra "—" por paridad con Ingresos/Egresos. En modo "por dia" la fila es
// un dia agregado (sin hora puntual) -> tambien "—".
const formatFecha = (f) => (f ? String(f).slice(0, 10) : '');
const formatHora = (f) => {
  if (!f) return '—';
  const s = String(f);
  const m = (s.includes('T') ? s.slice(11) : s).match(/(\d{1,2}):(\d{2})/);
  if (!m) return '—';
  let hh = parseInt(m[1], 10);
  const mm = m[2];
  if (hh === 0 && mm === '00') return '—';
  const ampm = hh >= 12 ? 'p. m.' : 'a. m.';
  hh = hh % 12; if (hh === 0) hh = 12;
  return `${hh}:${mm} ${ampm}`;
};

export async function renderCreditos(container) {
  const state = {
    items: [],
    totales: { abonosTotal: 0, numAbonos: 0 },
    porMedio: {},
    totalCount: 0,
    // Default 'total': la data de abonos es historica (mar–jun); con 'mes'=junio
    // la vista saldria casi vacia. Paridad con Ingresos/Egresos.
    filters: { period: 'total', medio: '', from: '', to: '' },
    groupBy: 'dia',
    page: 1,
    loading: false,
    error: null
  };

  container.innerHTML = `
    <section class="artifact-view creditos-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>📒 Créditos</h1>
          <p style="margin:4px 0 0;color:#8b949e;font-size:0.875rem">
            Abonos de crédito registrados (pagos a fiado)
          </p>
        </div>
        <div data-cv-poll style="font-size:0.875rem;color:#8b949e">⟳ cargando…</div>
      </div>

      <div style="margin-top:12px;padding:10px 14px;background:#1f6feb1a;border:1px dashed #1f6feb;border-radius:6px;color:#79c0ff;font-size:0.875rem;line-height:1.5">
        ℹ <strong>Versión 1: muestra abonos, no saldos.</strong>
        <span style="color:#8b949e">Aquí ves los pagos a crédito que entraron. El crédito completo (cliente, monto original, saldo pendiente) aparecerá cuando el Desktop empiece a sincronizar los créditos.</span>
      </div>

      <form data-cv-filters style="display:flex;flex-wrap:wrap;gap:10px;align-items:flex-end;margin-top:14px;
                                   padding:12px;background:#161b22;border:1px solid #30363d;border-radius:8px">
        ${renderFilterFields(state.filters)}
        <div data-cv-groupby style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
          Vista
          <div style="display:flex;border:1px solid #30363d;border-radius:6px;overflow:hidden">
            ${GROUP_MODES.map(g => `
              <button type="button" data-cv-mode="${g.code}"
                      style="padding:6px 12px;border:0;cursor:pointer;font-size:0.875rem;
                             background:${g.code === state.groupBy ? '#1f6feb' : '#0d1117'};
                             color:${g.code === state.groupBy ? '#fff' : '#8b949e'}">${g.label}</button>
            `).join('')}
          </div>
        </div>
        <button type="submit" class="btn-primary btn-sm">Aplicar</button>
        <button type="button" data-cv-reset class="btn-ghost btn-sm">Limpiar</button>
      </form>

      <div data-cv-totales style="display:flex;gap:14px;margin-top:14px;flex-wrap:wrap"></div>

      <div data-cv-tablewrap style="margin-top:14px;border:1px solid #30363d;border-radius:8px;overflow:hidden;background:#0d1117">
        <table style="width:100%;border-collapse:collapse;font-size:0.875rem">
          <thead style="background:#161b22" data-cv-thead></thead>
          <tbody data-cv-tbody></tbody>
        </table>
      </div>

      <div data-cv-pager style="display:flex;gap:10px;align-items:center;justify-content:flex-end;margin-top:10px"></div>
    </section>
  `;

  // ============== Wiring ==============

  const $form = container.querySelector('[data-cv-filters]');
  const $reset = container.querySelector('[data-cv-reset]');
  const $thead = container.querySelector('[data-cv-thead]');
  const $tbody = container.querySelector('[data-cv-tbody]');
  const $totales = container.querySelector('[data-cv-totales]');
  const $poll = container.querySelector('[data-cv-poll]');
  const $pager = container.querySelector('[data-cv-pager]');

  $form.addEventListener('change', (e) => {
    if (e.target.name === 'period') updatePeriodVisibility(container, e.target.value);
  });
  $form.addEventListener('submit', (e) => {
    e.preventDefault();
    Object.assign(state.filters, readFilters(container));
    state.page = 1;
    loadList();
  });
  $reset.addEventListener('click', () => {
    state.filters = { period: 'total', medio: '', from: '', to: '' };
    state.page = 1;
    $form.reset();
    container.querySelectorAll('[name="period"]').forEach(el => { el.value = 'total'; });
    updatePeriodVisibility(container, 'total');
    loadList();
  });
  container.querySelectorAll('[data-cv-mode]').forEach(btn => {
    btn.addEventListener('click', () => {
      const mode = btn.dataset.cvMode;
      if (mode === state.groupBy) return;
      state.groupBy = mode;
      state.page = 1;
      container.querySelectorAll('[data-cv-mode]').forEach(b => {
        const active = b.dataset.cvMode === mode;
        b.style.background = active ? '#1f6feb' : '#0d1117';
        b.style.color = active ? '#fff' : '#8b949e';
      });
      loadList();
    });
  });

  await loadList();

  // ============== Funciones internas ==============

  async function loadList() {
    const businessId = getSelectedBusiness();
    renderHead();
    if (!businessId) {
      $tbody.innerHTML = `<tr><td colspan="${colCount()}" style="padding:20px;text-align:center;color:#d29922">Selecciona un negocio en la barra superior</td></tr>`;
      $totales.innerHTML = '';
      $pager.innerHTML = '';
      $poll.textContent = '⚠ sin negocio';
      return;
    }
    state.loading = true;
    $poll.textContent = '⟳ cargando…';
    const res = await FinanzasApiClient.listCreditos(businessId, {
      ...state.filters,
      groupBy: state.groupBy,
      page: state.page,
      pageSize: PAGE_SIZE
    });
    state.loading = false;
    if (!res.ok) {
      state.error = res.error;
      $tbody.innerHTML = `<tr><td colspan="${colCount()}" style="padding:20px;text-align:center;color:#f85149">Error: ${escHtml(JSON.stringify(res.error || {}).slice(0, 120))}</td></tr>`;
      $poll.textContent = '⚠ error';
      return;
    }
    const payload = res.data?.data ?? res.data ?? {};
    state.items = Array.isArray(payload.items) ? payload.items : [];
    state.totales = payload.totales || { abonosTotal: sumMontos(state.items), numAbonos: state.items.length };
    state.porMedio = payload.porMedio || {};
    state.totalCount = Number(payload.totalCount) || state.items.length;
    state.error = null;
    renderRows();
    renderTotales(payload.limitacion_v1 || payload._hint);
    renderPager();
    const unidad = state.groupBy === 'dia' ? 'día(s)' : 'abono(s)';
    $poll.textContent = `✓ ${state.totalCount} ${unidad} · ${formatearFecha(new Date(), {}, { parte: 'hora' })}`;
  }

  function colCount() {
    return state.groupBy === 'dia' ? 4 : 5;
  }

  function renderHead() {
    const th = (label, right = false) =>
      `<th style="padding:8px 10px;text-align:${right ? 'right' : 'left'};color:#8b949e;font-size:0.875rem;text-transform:uppercase;border-bottom:1px solid #30363d">${label}</th>`;
    // REDIRECT #2160: paridad con Ingresos/Egresos -> Fecha | Hora | ... | Total.
    $thead.innerHTML = state.groupBy === 'dia'
      ? `<tr>${th('Fecha')}${th('Hora')}${th('Medio')}${th('Total abonado', true)}</tr>`
      : `<tr>${th('Fecha')}${th('Hora')}${th('Medio')}${th('Origen')}${th('Monto', true)}</tr>`;
  }

  function renderRows() {
    if (state.items.length === 0) {
      $tbody.innerHTML = `<tr><td colspan="${colCount()}" style="padding:24px;text-align:center;color:#8b949e">
        Sin abonos de crédito para los filtros aplicados.
      </td></tr>`;
      return;
    }
    $tbody.innerHTML = state.items.map(it => renderRow(it)).join('');
  }

  function renderMedio(it) {
    const medio = (it.medio || '-').toLowerCase();
    const color = MEDIO_COLOR[medio] || '#8b949e';
    const pagos = Number(it.num_pagos ?? it.numPagos) || 0;
    const extra = (state.groupBy === 'dia' && pagos > 1) ? ` · ${pagos} abonos` : '';
    return `<span style="background:${color}22;color:${color};padding:2px 8px;border-radius:10px;font-size:0.875rem;font-weight:600">${escHtml(medio)}${extra}</span>`;
  }

  function renderRow(it) {
    const fechaRaw = it.fecha ?? it.fechaOperativa;
    const fecha = formatFecha(fechaRaw);
    const hora = formatHora(fechaRaw); // venta_pagos.fecha es DATE -> "—" (paridad)
    const monto = formatCurrency(it.monto ?? it.total ?? 0);
    const tdBase = 'padding:8px 10px;color:#c9d1d9';
    const tdHora = 'padding:8px 10px;color:#8b949e;font-size:0.875rem';
    if (state.groupBy === 'dia') {
      return `
        <tr style="border-bottom:1px solid #21262d">
          <td style="${tdBase}">${escHtml(fecha)}</td>
          <td style="${tdHora}">${escHtml(hora)}</td>
          <td style="padding:8px 10px">${renderMedio(it)}</td>
          <td style="padding:8px 10px;text-align:right;color:#3fb950;font-weight:600">$${monto}</td>
        </tr>
      `;
    }
    return `
      <tr style="border-bottom:1px solid #21262d">
        <td style="${tdBase}">${escHtml(fecha)}</td>
        <td style="${tdHora}">${escHtml(hora)}</td>
        <td style="padding:8px 10px">${renderMedio(it)}</td>
        <td style="${tdBase}">${escHtml(it.origen || '-')}</td>
        <td style="padding:8px 10px;text-align:right;color:#3fb950;font-weight:600">$${monto}</td>
      </tr>
    `;
  }

  function renderTotales(limitacion) {
    const total = Number(state.totales.abonosTotal ?? state.totales.totalAbonado ?? 0);
    const num = Number(state.totales.numAbonos ?? 0);
    const medios = Object.entries(state.porMedio).sort(([a], [b]) => a.localeCompare(b));
    const card = (label, value, color, isMoney = true) => `
      <div style="flex:1;min-width:160px;padding:12px;background:#161b22;border:1px solid #30363d;border-left:3px solid ${color};border-radius:6px">
        <div style="font-size:0.875rem;color:#8b949e;text-transform:uppercase">${escHtml(label)}</div>
        <div style="font-size:1.2rem;color:${color};font-weight:700;margin-top:4px">${isMoney ? '$' : ''}${formatCurrency(value)}</div>
      </div>`;
    $totales.innerHTML = `
      ${card('Total abonado', total, '#3fb950')}
      ${card('N° abonos', num, '#58a6ff', false)}
      ${medios.map(([medio, monto]) => card(medio, monto, MEDIO_COLOR[medio] || '#58a6ff')).join('')}
      ${limitacion ? `<div style="flex:1;min-width:240px;padding:10px;background:#0d1117;border:1px dashed #484f58;border-radius:6px;color:#8b949e;font-size:0.875rem">ℹ ${escHtml(limitacion)}</div>` : ''}
    `;
  }

  function renderPager() {
    const totalPages = Math.max(1, Math.ceil(state.totalCount / PAGE_SIZE));
    if (totalPages <= 1) { $pager.innerHTML = ''; return; }
    $pager.innerHTML = `
      <button type="button" data-cv-prev class="btn-ghost btn-sm" ${state.page <= 1 ? 'disabled' : ''}>&lsaquo; Anterior</button>
      <span style="font-size:0.875rem;color:#8b949e">Página ${state.page} de ${totalPages}</span>
      <button type="button" data-cv-next class="btn-ghost btn-sm" ${state.page >= totalPages ? 'disabled' : ''}>Siguiente &rsaquo;</button>
    `;
    $pager.querySelector('[data-cv-prev]')?.addEventListener('click', () => {
      if (state.page > 1) { state.page--; loadList(); }
    });
    $pager.querySelector('[data-cv-next]')?.addEventListener('click', () => {
      if (state.page < totalPages) { state.page++; loadList(); }
    });
  }
}

// ============== Helpers compartidos ==============

function renderFilterFields(filters) {
  const periodOpts = PERIODS.map(p => `<option value="${p.code}" ${p.code === filters.period ? 'selected' : ''}>${p.label}</option>`).join('');
  const medioOpts = MEDIOS.map(m => `<option value="${m.code}" ${m.code === filters.medio ? 'selected' : ''}>${m.label}</option>`).join('');
  const rangoVisible = filters.period === 'rango';
  return `
    <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
      Periodo
      <select name="period" class="form-input form-input-sm" style="min-width:120px;font-size:14px">${periodOpts}</select>
    </label>
    <label data-cv-rango-from style="display:${rangoVisible ? 'flex' : 'none'};flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
      Fecha ini
      <input type="date" name="from" class="form-input form-input-sm" value="${filters.from || ''}"/>
    </label>
    <label data-cv-rango-to style="display:${rangoVisible ? 'flex' : 'none'};flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
      Fecha fin
      <input type="date" name="to" class="form-input form-input-sm" value="${filters.to || ''}"/>
    </label>
    <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
      Medio
      <select name="medio" class="form-input form-input-sm" style="min-width:130px;font-size:14px">${medioOpts}</select>
    </label>
  `;
}

function readFilters(container) {
  const $form = container.querySelector('[data-cv-filters]');
  return {
    period: $form.querySelector('[name="period"]')?.value || 'total',
    medio:  $form.querySelector('[name="medio"]')?.value || '',
    from:   $form.querySelector('[name="from"]')?.value || '',
    to:     $form.querySelector('[name="to"]')?.value || ''
  };
}

function updatePeriodVisibility(container, period) {
  const isRango = period === 'rango';
  const $from = container.querySelector('[data-cv-rango-from]');
  const $to = container.querySelector('[data-cv-rango-to]');
  if ($from) $from.style.display = isRango ? 'flex' : 'none';
  if ($to) $to.style.display = isRango ? 'flex' : 'none';
}

function sumMontos(items) {
  return items.reduce((s, it) => s + (Number(it.monto ?? it.total) || 0), 0);
}
